import React, { useContext, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronDown,
  FaChevronRight,
  FaSignOutAlt,
} from "react-icons/fa";
import {
  MdAssignmentInd,
  MdBadge,
  MdChecklist,
  MdFolder,
  MdGroup,
  MdHowToReg,
  MdPersonSearch,
  MdSettings,
} from "react-icons/md";
import { canUserAccessMenu } from "../constants/userMenuAccess";

const Sidebar = ({
  collapsed,
  toggled,
  handleToggleSidebar,
  handleCollapsedChange,
}) => {
  const { user, dispatch, setting } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({
    master: true,
    survey: true,
    utility: true,
  });
  const sidebarSoftwareName = (setting.namaSoftware || "")
    .replace(/TECH\s*KU/gi, "MTS");

  const menuTree = useMemo(
    () => [
      {
        id: "master",
        label: "Master",
        icon: <MdFolder />,
        children: [
          {
            id: "surveyor",
            label: "Surveyor",
            icon: <MdAssignmentInd />,
            to: "/surveyor",
            access: "surveyor",
          },
          {
            id: "kolektor",
            label: "Kolektor",
            icon: <MdBadge />,
            to: "/kolektor",
            access: "kolektor",
          },
        ],
      },
      {
        id: "survey",
        label: "Survey",
        icon: <MdChecklist />,
        children: [
          {
            id: "permohonan",
            label: "Entry Permohonan",
            icon: <MdChecklist />,
            to: "/permohonan",
            access: "permohonan",
          },
          {
            id: "namaPemohon",
            label: "Daftar Permohonan",
            icon: <MdAssignmentInd />,
            to: "/namaPemohon",
            access: "namaPemohon",
          },
          {
            id: "admin",
            label: "Data Permohonan",
            icon: <MdBadge />,
            to: "/dataPermohonan",
            access: "admin",
          },
          {
            id: "dataVerifikasi",
            label: "Data Verifikasi",
            icon: <MdHowToReg />,
            to: "/dataVerifikasi",
            access: "dataVerifikasi",
          },
          {
            id: "validasiDataVerifikator",
            label: "Validasi Data Verifikator",
            icon: <MdBadge />,
            to: "/validasiDataVerifikator",
            access: "validasiDataVerifikator",
          },
          {
            id: "daftarKunjungan",
            label: "Daftar Kunjungan",
            icon: <MdGroup />,
            to: "/daftarKunjungan",
            access: "daftarKunjungan",
          },
        ],
      },
      {
        id: "utility",
        label: "Utility",
        icon: <MdSettings />,
        children: [
          {
            id: "profilUser",
            label: "Profil User",
            icon: <MdPersonSearch />,
            to: "/profilUser",
            access: "profilUser",
          },
          {
            id: "daftarUser",
            label: "Daftar User",
            icon: <MdGroup />,
            to: "/daftarUser",
            access: "daftarUser",
          },
        ],
      },
    ],
    [],
  );

  const logoutButtonHandler = (event) => {
    event.preventDefault();
    dispatch({ type: "LOGOUT" });
    navigate("/");
  };

  const closeOnMobile = () => handleToggleSidebar(false);

  const sidebarToggleHandler = () => {
    if (window.matchMedia("(max-width: 860px)").matches) {
      handleToggleSidebar(false);
      return;
    }

    handleCollapsedChange();
  };

  const toggleMenu = (id) => {
    setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const hasVisibleChild = (item) => {
    if (!item.children) return canUserAccessMenu(user, item.access);
    return item.children.some(hasVisibleChild);
  };

  const isActiveBranch = (item) => {
    if (item.to) return location.pathname.startsWith(item.to);
    return item.children?.some(isActiveBranch);
  };

  const renderMenuItem = (item, depth = 0) => {
    if (!hasVisibleChild(item)) return null;

    if (item.children) {
      const open = openMenus[item.id] || isActiveBranch(item);
      return (
        <div key={item.id} className={`sidebar-node depth-${depth}`}>
          <button
            type="button"
            className={`sidebar-menu-button ${isActiveBranch(item) ? "active-branch" : ""}`}
            onClick={() => toggleMenu(item.id)}
            title={item.label}
          >
            <span className="sidebar-menu-icon">{item.icon}</span>
            {!collapsed && <span className="sidebar-menu-label">{item.label}</span>}
            {!collapsed && (
              <span className="sidebar-menu-chevron">
                {open ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            )}
          </button>
          {open && !collapsed && (
            <div className="sidebar-children">
              {item.children.map((child) => renderMenuItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.id}
        className={`sidebar-link depth-${depth}`}
        to={item.to}
        onClick={closeOnMobile}
        title={item.label}
      >
        <span className="sidebar-menu-icon">{item.icon}</span>
        {!collapsed && <span>{item.label}</span>}
      </NavLink>
    );
  };

  return (
    <>
      <button
        type="button"
        className={`sidebar-backdrop ${toggled ? "visible" : ""}`}
        onClick={closeOnMobile}
        aria-label="Tutup menu"
      />
      <aside
        className={`app-sidebar ${collapsed ? "collapsed" : ""} ${
          toggled ? "mobile-open" : ""
        }`}
      >
        <div className="sidebar-brand">
          <span className="brand-mark">MTS</span>
          {!collapsed && (
            <span className="brand-text">
              {setting.namaProgram || "SURVEY"} {sidebarSoftwareName}
            </span>
          )}
          <button
            className="sidebar-collapse-btn"
            type="button"
            onClick={sidebarToggleHandler}
            aria-label={toggled ? "Tutup menu" : "Toggle sidebar"}
          >
            {collapsed && !toggled ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
          </button>
        </div>

        <nav className="sidebar-nav">{menuTree.map((item) => renderMenuItem(item))}</nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <>
              <div className="sidebar-user">{user.username}</div>
              <div className="sidebar-branch">
                {user.cabang?.namaCabang || "CABANG"}
              </div>
            </>
          )}
          <button className="logout-btn" type="button" onClick={logoutButtonHandler}>
            {!collapsed && <span>Logout</span>}
            <FaSignOutAlt />
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
